import { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { categories } from '@/mocks/categories';
import { articlePreviews } from '@/mocks/articles';

const categoryIcons: Record<string, string> = {
  shoes: 'ri-footprint-line',
  hoodies: 'ri-t-shirt-line',
  tshirts: 'ri-shirt-line',
  jackets: 'ri-t-shirt-2-line',
  pants: 'ri-artboard-line',
  headwear: 'ri-glasses-line',
  sets: 'ri-stack-line',
  underwear: 'ri-user-line',
  jersey: 'ri-run-line',
  accessories: 'ri-gemini-line',
  bags: 'ri-shopping-bag-line',
  belts: 'ri-links-line',
  perfumes: 'ri-drop-line',
  other: 'ri-more-line',
};

const categoryImages: Record<string, string> = {
  shoes: 'https://readdy.ai/api/search-image?query=Single%20white%20modern%20sneaker%20with%20gum%20sole%20on%20warm%20beige%20background%2C%20minimalist%20product%20photography%20with%20soft%20diffused%20studio%20lighting%2C%20clean%20composition%2C%20fashion%20editorial%20aesthetic%2C%20neutral%20tones&width=120&height=120&seq=nav-cat-shoes-2026&orientation=squarish',
  hoodies: 'https://readdy.ai/api/search-image?query=Soft%20oatmeal%20colored%20hoodie%20folded%20neatly%20on%20beige%20linen%20fabric%20background%2C%20warm%20natural%20light%20from%20window%2C%20minimalist%20Scandinavian%20product%20photography%20style%2C%20cozy%20aesthetic&width=120&height=120&seq=nav-cat-hoodies-2026&orientation=squarish',
  tshirts: 'https://readdy.ai/api/search-image?query=Premium%20white%20cotton%20t-shirt%20neatly%20arranged%20on%20warm%20sand%20colored%20surface%2C%20soft%20overhead%20studio%20lighting%20with%20subtle%20shadow%2C%20high%20end%20minimalist%20product%20photography%2C%20clean%20modern%20aesthetic&width=120&height=120&seq=nav-cat-tshirts-2026&orientation=squarish',
  jackets: 'https://readdy.ai/api/search-image?query=Lightweight%20olive%20green%20bomber%20jacket%20displayed%20on%20cream%20background%2C%20editorial%20fashion%20product%20photography%20with%20gentle%20side%20lighting%2C%20soft%20fabric%20texture%20visible%2C%20minimalist%20composition&width=120&height=120&seq=nav-cat-jackets-2026&orientation=squarish',
  pants: 'https://readdy.ai/api/search-image?query=Tailored%20camel%20colored%20trousers%20laid%20flat%20on%20warm%20beige%20linen%20surface%2C%20soft%20natural%20lighting%20with%20gentle%20shadows%2C%20minimalist%20fashion%20product%20photography%2C%20clean%20editorial%20aesthetic&width=120&height=120&seq=nav-cat-pants-2026&orientation=squarish',
  headwear: 'https://readdy.ai/api/search-image?query=Neutral%20sand%20colored%20baseball%20cap%20with%20subtle%20texture%20on%20cream%20background%2C%20soft%20diffused%20studio%20lighting%2C%20minimalist%20product%20photography%2C%20modern%20casual%20aesthetic&width=120&height=120&seq=nav-cat-headwear-2026&orientation=squarish',
  sets: 'https://readdy.ai/api/search-image?query=Matching%20beige%20tracksuit%20set%20folded%20together%20on%20warm%20neutral%20background%2C%20soft%20natural%20lighting%2C%20minimalist%20fashion%20photography%2C%20coordinated%20outfit%20flat%20lay&width=120&height=120&seq=nav-cat-sets-2026&orientation=squarish',
  underwear: 'https://readdy.ai/api/search-image?query=Premium%20cotton%20underwear%20in%20soft%20grey%20tone%20on%20warm%20beige%20background%2C%20clean%20minimalist%20product%20photography%20with%20gentle%20shadows%2C%20modern%20intimate%20apparel%20aesthetic&width=120&height=120&seq=nav-cat-underwear-2026&orientation=squarish',
  jersey: 'https://readdy.ai/api/search-image?query=Modern%20athletic%20sports%20jersey%20in%20deep%20forest%20green%20on%20cream%20background%2C%20clean%20product%20photography%20with%20soft%20studio%20lighting%2C%20sportswear%20editorial%20style%2C%20minimalist%20composition&width=120&height=120&seq=nav-cat-jersey-2026&orientation=squarish',
  accessories: 'https://readdy.ai/api/search-image?query=Elegant%20gold%20toned%20minimalist%20watch%20on%20warm%20beige%20surface%2C%20soft%20natural%20lighting%20with%20subtle%20reflections%2C%20luxury%20product%20photography%2C%20clean%20modern%20aesthetic%2C%20refined%20composition&width=120&height=120&seq=nav-cat-accessories-2026&orientation=squarish',
  bags: 'https://readdy.ai/api/search-image?query=Structured%20tan%20leather%20crossbody%20bag%20on%20cream%20background%2C%20soft%20studio%20lighting%20with%20gentle%20shadows%2C%20minimalist%20fashion%20accessory%20photography%2C%20clean%20editorial%20style&width=120&height=120&seq=nav-cat-bags-2026&orientation=squarish',
  belts: 'https://readdy.ai/api/search-image?query=Braided%20natural%20leather%20belt%20coiled%20elegantly%20on%20warm%20beige%20surface%2C%20soft%20diffused%20lighting%2C%20minimalist%20accessory%20product%20photography%2C%20refined%20casual%20aesthetic&width=120&height=120&seq=nav-cat-belts-2026&orientation=squarish',
  perfumes: 'https://readdy.ai/api/search-image?query=Sleek%20minimalist%20perfume%20bottle%20with%20amber%20liquid%20on%20cream%20background%2C%20soft%20side%20lighting%20creating%20elegant%20reflections%2C%20luxury%20fragrance%20product%20photography%2C%20sophisticated%20aesthetic&width=120&height=120&seq=nav-cat-perfumes-2026&orientation=squarish',
  other: 'https://readdy.ai/api/search-image?query=Various%20small%20fashion%20accessories%20scattered%20artfully%20on%20warm%20beige%20surface%20including%20sunglasses%20and%20keychain%2C%20soft%20natural%20lighting%2C%20editorial%20flat%20lay%20photography%2C%20minimalist%20aesthetic&width=120&height=120&seq=nav-cat-other-2026&orientation=squarish',
};

export default function Navbar() {
  const { t } = useTranslation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [articleDropdownOpen, setArticleDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const articleDropdownRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const hamburgerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
      if (articleDropdownRef.current && !articleDropdownRef.current.contains(e.target as Node)) {
        setArticleDropdownOpen(false);
      }
      if (
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(e.target as Node) &&
        hamburgerRef.current &&
        !hamburgerRef.current.contains(e.target as Node)
      ) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCategoryClick = useCallback(() => {
    setDropdownOpen(false);
    setMobileMenuOpen(false);
  }, []);

  const handleArticleClick = useCallback(() => {
    setArticleDropdownOpen(false);
    setMobileMenuOpen(false);
  }, []);

  const closeAll = useCallback(() => {
    setMobileMenuOpen(false);
    setDropdownOpen(false);
    setArticleDropdownOpen(false);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/60'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-6 md:px-10">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-2 group flex-shrink-0"
          >
            <span className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 tracking-tight">
              Itaobuy <span className="text-primary-500">Spread</span>
            </span>
          </Link>

          {/* Center: Desktop Navigation */}
          <div className="hidden md:flex items-center gap-2">
            {/* Home Button */}
            <Link
              to="/"
              className="px-4 py-2 rounded-full text-sm font-label font-medium text-foreground-700 hover:bg-background-100 hover:text-foreground-900 transition-all duration-200 whitespace-nowrap"
            >
              {t('nav.home')}
            </Link>

            {/* Category Dropdown */}
            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => { setDropdownOpen(!dropdownOpen); setArticleDropdownOpen(false); }}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-background-100 hover:bg-background-200 text-foreground-800 font-label text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-grid-line w-4 h-4 flex items-center justify-center"></i>
                <span>{t('nav.categories')}</span>
                <i className={`ri-arrow-down-s-line w-4 h-4 flex items-center justify-center transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 w-[720px] bg-background-50 rounded-xl border border-background-200/60 shadow-lg py-4 animate-fade-in">
                  <div className="grid grid-cols-4 gap-1 px-3">
                    {categories.map((cat) => (
                      <a
                        key={cat.id}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        onClick={handleCategoryClick}
                        className="flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-background-100 transition-colors duration-150 group cursor-pointer"
                      >
                        <div className="w-16 h-16 rounded-xl overflow-hidden bg-background-100">
                          <img
                            src={categoryImages[cat.id] || categoryImages.other}
                            alt={t(cat.nameKey)}
                            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                        <span className="text-xs text-foreground-800 font-label font-medium whitespace-nowrap text-center">
                          {t(cat.nameKey)}
                        </span>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Article Dropdown */}
            <div ref={articleDropdownRef} className="relative">
              <button
                onClick={() => { setArticleDropdownOpen(!articleDropdownOpen); setDropdownOpen(false); }}
                className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-label font-medium text-foreground-700 hover:bg-background-100 hover:text-foreground-900 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-article-line w-4 h-4 flex items-center justify-center"></i>
                <span>Guide</span>
                <i className={`ri-arrow-down-s-line w-4 h-4 flex items-center justify-center transition-transform duration-200 ${articleDropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {articleDropdownOpen && (
                <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 w-[480px] bg-background-50 rounded-xl border border-background-200/60 shadow-lg py-3 animate-fade-in">
                  <div className="px-3 pb-2 mb-2 border-b border-background-100">
                    <span className="text-xs font-label font-semibold text-foreground-400 uppercase tracking-wider px-2">Guide e Articoli</span>
                  </div>
                  <div className="grid grid-cols-1 gap-0.5 px-2">
                    <Link to="/articolo/guida-completa-itaobuy-spreadsheet" onClick={handleArticleClick} className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-background-100 transition-colors cursor-pointer">
                      <span className="w-8 h-8 rounded-full bg-primary-50 flex items-center justify-center flex-shrink-0">
                        <i className="ri-book-open-line text-primary-500 text-sm w-4 h-4 flex items-center justify-center"></i>
                      </span>
                      <div className="min-w-0">
                        <span className="text-sm text-foreground-800 font-label font-medium">Guida Completa</span>
                        <p className="text-xs text-foreground-400 truncate">Come usare, categorie e consigli</p>
                      </div>
                    </Link>
                    {articlePreviews.map((article) => (
                      <Link
                        key={article.id}
                        to={article.slug}
                        onClick={handleArticleClick}
                        className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
                      >
                        <span className="w-8 h-8 rounded-full bg-secondary-50 flex items-center justify-center flex-shrink-0">
                          <i className="ri-file-text-line text-secondary-500 text-sm w-4 h-4 flex items-center justify-center"></i>
                        </span>
                        <div className="min-w-0">
                          <span className="text-sm text-foreground-800 font-label font-medium truncate block">{article.title}</span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Guida Acquisti button */}
            <Link
              to="/articolo/come-usare-itaobuy-spreadsheet"
              className="px-4 py-2 rounded-full text-sm font-label font-medium text-foreground-700 hover:bg-background-100 hover:text-foreground-900 transition-all duration-200 whitespace-nowrap"
            >
              Guida Acquisti
            </Link>
          </div>

          {/* Right: CTA Buttons */}
          <div className="flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="hidden sm:inline-flex items-center px-5 py-2.5 rounded-full border border-foreground-200 text-foreground-800 font-label text-sm font-medium hover:bg-foreground-50 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              {t('nav.itaobuy_sheet')}
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-primary-500 text-white font-label text-sm font-medium hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              {t('nav.buy_now')}
              <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
            </a>

            {/* Mobile menu button */}
            <button
              ref={hamburgerRef}
              onClick={() => {
                if (mobileMenuOpen) {
                  closeAll();
                } else {
                  setDropdownOpen(false);
                  setArticleDropdownOpen(false);
                  setMobileMenuOpen(true);
                }
              }}
              className="md:hidden w-9 h-9 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
              aria-label={mobileMenuOpen ? 'Chiudi menu' : 'Apri menu'}
            >
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl text-foreground-800 w-5 h-5 flex items-center justify-center`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div ref={mobileMenuRef} className="md:hidden pb-6 animate-fade-in border-t border-background-100 mt-2 pt-4 overflow-y-auto max-h-[calc(100vh-80px)] overscroll-contain">
            {/* Mobile Quick Links */}
            <div className="flex items-center gap-2 mb-4 px-2">
              <Link
                to="/"
                onClick={closeAll}
                className="flex-1 text-center px-4 py-2.5 rounded-full bg-primary-50 text-primary-700 font-label text-sm font-medium"
              >
                {t('nav.home')}
              </Link>
              <Link
                to="/articolo/come-usare-itaobuy-spreadsheet"
                onClick={closeAll}
                className="flex-1 text-center px-4 py-2.5 rounded-full bg-secondary-50 text-secondary-700 font-label text-sm font-medium"
              >
                Guida Acquisti
              </Link>
            </div>

            {/* Mobile Categories - Enhanced with images */}
            <div className="mb-4">
              <h4 className="text-xs font-label font-semibold text-foreground-400 uppercase tracking-wider px-2 mb-2">{t('nav.categories')}</h4>
              <div className="grid grid-cols-2 gap-1.5">
                {categories.map((cat) => (
                  <a
                    key={cat.id}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={closeAll}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    <div className="w-10 h-10 rounded-lg overflow-hidden bg-background-100 flex-shrink-0">
                      <img
                        src={categoryImages[cat.id] || categoryImages.other}
                        alt={t(cat.nameKey)}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <span className="text-sm text-foreground-800 font-label font-medium">
                      {t(cat.nameKey)}
                    </span>
                  </a>
                ))}
              </div>
            </div>

            {/* Mobile Articles */}
            <div>
              <h4 className="text-xs font-label font-semibold text-foreground-400 uppercase tracking-wider px-2 mb-2">Guide e Articoli</h4>
              <div className="space-y-0.5">
                <Link
                  to="/articolo/guida-completa-itaobuy-spreadsheet"
                  onClick={closeAll}
                  className="flex items-center gap-2 px-3 py-2.5 rounded-lg hover:bg-background-100 transition-colors"
                >
                  <i className="ri-book-open-line text-primary-500 w-4 h-4 flex items-center justify-center"></i>
                  <span className="text-sm text-foreground-800 font-label font-medium">Guida Completa</span>
                </Link>
                {articlePreviews.map((article) => (
                  <Link
                    key={article.id}
                    to={article.slug}
                    onClick={closeAll}
                    className="flex items-center gap-2 px-3 py-2.5 rounded-lg hover:bg-background-100 transition-colors"
                  >
                    <i className="ri-file-text-line text-secondary-500 w-4 h-4 flex items-center justify-center"></i>
                    <span className="text-sm text-foreground-800 font-label font-medium truncate">{article.title}</span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}